"use client";

import {useState, useRef, useEffect} from "react";
import {toast} from "@/components/commons/one-toast-container";
import {RefreshCw, Paperclip, AlertTriangle} from "lucide-react";
import {FileUploadHandler} from "@/components/commons/file-upload-handler";
import {useFileUpload} from "@/components/commons/use-file-upload";
import {uploadFileToS3, isImageFile, isTextFile, convertFileToBase64} from "@/lib/file-utils";
import {useRouter} from "next/navigation";
import {SupportedModules, useUser} from "@/components/commons/user-context";
import {SAMPLE_PROMPTS, AGENT_SAMPLE_PROMPTS, WORKFLOW_SAMPLE_PROMPTS, APP_SAMPLE_PROMPTS} from "@/lib/uiConstants";
import {DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuGroup} from "@/components/ui/dropdown-menu";
import {Badge} from "@/components/ui/badge";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";

// Component for prompt chips with mouse-following sparkle
const PromptChip = ({sample, onClick}: {sample: {label: string; prompt: string}; onClick: () => void}) => {
  const [mousePosition, setMousePosition] = useState({x: 0, y: 0});

  const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  return (
    <button
      onClick={onClick}
      onMouseMove={handleMouseMove}
      className="group relative px-4 py-1.5 bg-white text-gray-600 hover:text-gray-900 rounded-xl text-xs font-medium transition-all duration-200 border border-gray-200/70 hover:border-gray-300 shadow-sm hover:shadow-md overflow-hidden">
      <span className="relative z-10">{sample.label}</span>
      {/* Glossy shine effect */}
      <div className="absolute inset-0 bg-gradient-to-t from-transparent to-white/30 opacity-40" />
      {/* Mouse-following sparkle */}
      <div
        className="absolute w-32 h-32 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(147,197,253,0.25) 0%, transparent 70%)",
          left: mousePosition.x - 64,
          top: mousePosition.y - 64,
        }}
      />
      {/* Top highlight for glass effect */}
      <div className="absolute inset-x-0 top-0 h-[0.5px] bg-gradient-to-r from-transparent via-white/60 to-transparent" />
    </button>
  );
};

export type BuildMode = "Preview" | "App" | "Agents" | "Workflows";

interface BuildPromptProps {
  onSubmit?: (prompt: string, buildMode: BuildMode, attachedFiles: any[]) => Promise<void>;
  onAppCreated?: () => void;
  currentMode?: BuildMode;
  showHero?: boolean;
  showSamplePrompts?: boolean;
  heroTitle?: string;
  heroTagline?: string;
}

export default function BuildPrompt({onSubmit, onAppCreated, currentMode = "Preview", showHero = true, showSamplePrompts = true, heroTitle, heroTagline}: BuildPromptProps) {
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [buildMode, setBuildMode] = useState<BuildMode>(currentMode);
  const [isUploading, setIsUploading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const {user} = useUser();
  const tenantId = user?.tenants?.find(t => t.selected)?._id;
  const currentTenant = user?.tenants?.find(t => t.selected);

  // Use the file upload hook
  const {attachedFiles, setAttachedFiles, isDragOver, handleFileSelect, handlers} = useFileUpload();

  // Update build mode when currentMode prop changes
  useEffect(() => {
    setBuildMode(currentMode);
  }, [currentMode]);

  // Load prompt from localStorage on mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      // First check for URL query param (only on home page)
      const urlParams = new URLSearchParams(window.location.search);
      const promptParam = urlParams.get("prompt");
      if (promptParam) {
        setPrompt(promptParam);
        localStorage.setItem("pendingPrompt", promptParam);
        // Remove query param from URL
        const newUrl = window.location.pathname;
        window.history.replaceState({}, "", newUrl);
      } else {
        // Load from localStorage if no URL param
        const savedPrompt = localStorage.getItem("pendingPrompt");
        if (savedPrompt) {
          setPrompt(savedPrompt);
        }
      }
    }
  }, []);

  // Save prompt to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== "undefined") {
      if (prompt.trim()) {
        localStorage.setItem("pendingPrompt", prompt);
      } else {
        localStorage.removeItem("pendingPrompt");
      }
    }
  }, [prompt]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [prompt]);

  const handleBuildModeChange = (mode: BuildMode) => {
    setBuildMode(mode);
    // If switching to Agents or Workflows, navigate to their pages
    if (mode === "Agents") {
      router.push("/dashboard/agents");
    } else if (mode === "Workflows") {
      router.push("/dashboard/workflows");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim() || isGenerating) return;

    // Check if user is logged in
    if (!user) {
      // Prompt is already saved to localStorage, just redirect to login
      router.push("/login");
      return;
    }

    setIsGenerating(true);

    try {
      // Upload files to S3 and prepare file data with both URLs and content
      let fileData: any[] = [];

      if (attachedFiles.length > 0) {
        setIsUploading(true);
        try {
          fileData = await Promise.all(
            attachedFiles.map(async file => {
              try {
                // Upload to S3 for storage
                const url = await uploadFileToS3(file.file, user?.jwtToken || "");

                // Convert file to base64 for AI processing
                let content = null;
                try {
                  content = await convertFileToBase64(file.file);
                } catch (error) {
                  console.warn(`Could not encode file ${file.name}:`, error);
                  content = `[File content could not be encoded: ${file.name}]`;
                }

                return {
                  name: file.name,
                  size: file.size,
                  type: file.type,
                  mediaType: file.type,
                  url: url, // S3 URL for storage
                  content: content, // Base64 data URL for AI processing
                  isImage: isImageFile(file.type),
                  isText: isTextFile(file.type, file.name),
                };
              } catch (error) {
                console.error(`Failed to upload ${file.name}:`, error);
                toast.error(`Failed to upload ${file.name}`);
                throw error;
              }
            })
          );
        } catch (error) {
          console.error("File upload failed:", error);
          toast.error("Failed to upload files");
          setIsGenerating(false);
          setIsUploading(false);
          return;
        } finally {
          setIsUploading(false);
        }
      }

      // If custom onSubmit is provided, use it (for Agents/Workflows)
      if (onSubmit) {
        await onSubmit(prompt, buildMode, fileData);
        setPrompt("");
        setAttachedFiles([]);
        localStorage.removeItem("pendingPrompt");
        setIsGenerating(false);
        return;
      }

      // Default behavior for Preview and App modes
      const res = await fetch("/api/apps", {
        method: "POST",
        headers: {"Content-Type": "application/json", Authorization: `Bearer ${user.jwtToken}`},
        body: JSON.stringify({
          prompt,
          tenantId,
          buildMode: buildMode.toLowerCase(),
          attachedFiles: fileData,
        }),
      });

      if (!res.ok) {
        console.log("Failed to create project");
        const errorData = await res.text();
        toast.error(errorData || "Failed to create project");
        setIsGenerating(false);
        return;
      }

      const data = await res.json();

      // Clear the saved prompt and files after successful submission
      localStorage.removeItem("pendingPrompt");
      setAttachedFiles([]);

      // Call the callback if provided (for refreshing apps list)
      if (onAppCreated) {
        onAppCreated();
      }

      // Navigate to appropriate page based on build mode
      const basePath = buildMode === "App" ? "/app" : "/preview";
      router.push(`${basePath}/${data._id}`);
    } catch (error) {
      console.error("Project creation error:", error);
      toast.error(error instanceof Error ? error.message : "An unexpected error occurred");
      setIsGenerating(false);
    } finally {
      setPrompt("");
    }
  };

  // Get the appropriate title and tagline
  const getHeroContent = () => {
    if (heroTitle && heroTagline) {
      return {title: heroTitle, tagline: heroTagline};
    }

    switch (currentMode) {
      case "Agents":
        return {
          title: "Build Finance Agents",
          tagline: "Reimagine finance, one agent at a time",
        };
      case "Workflows":
        return {
          title: "Build Finance Workflows",
          tagline: "Reimagine finance, one workflow at a time",
        };
      default:
        return {
          title: "Build Finance Apps",
          tagline: "Reimagine finance, one app at a time",
        };
    }
  };

  const {title, tagline} = getHeroContent();

  // Get the appropriate sample prompts based on mode
  const getSamplePrompts = () => {
    switch (buildMode) {
      case "Agents":
        return AGENT_SAMPLE_PROMPTS;
      case "Workflows":
        return WORKFLOW_SAMPLE_PROMPTS;
      case "App":
        return APP_SAMPLE_PROMPTS;
      default:
        return SAMPLE_PROMPTS;
    }
  };

  const samplePrompts = getSamplePrompts();

  // Map buildMode to module name
  const getModuleFromBuildMode = (mode: BuildMode): SupportedModules => {
    switch (mode) {
      case "Preview":
        return "preview";
      case "App":
        return "apps";
      case "Agents":
        return "agents";
      case "Workflows":
        return "workflows";
      default:
        return "preview";
    }
  };

  // Check if current module is enabled
  const currentModule = getModuleFromBuildMode(buildMode);
  const enabledModules = currentTenant?.modules || [];
  const isModuleEnabled = enabledModules.includes(currentModule);

  return (
    <div className="max-w-4xl w-full">
      {/* Hero Text - Always show to maintain consistent layout */}
      {showHero && (
        <div className="text-center mb-8">
          <h1 className="text-[2.75rem] font-semibold text-gray-900 mb-2">
            {title} <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent font-bold">with AI</span>
          </h1>
          <p className="text-xl text-gray-500">{tagline}</p>
          {/* Warning Banner - Show if module is not enabled */}
          {user && !isModuleEnabled && (
            <div className="max-w-3xl mx-auto mt-6 mb-6 bg-yellow-50 border border-yellow-200 rounded-lg px-5 py-3.5 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
              <p className="text-sm font-medium text-yellow-800">Selected module is not enabled for your tenant.</p>
            </div>
          )}
        </div>
      )}

      {/* Prompt Input */}
      <form onSubmit={handleSubmit} className="max-w-3xl w-full mx-auto mb-8">
        <div className="relative group">
          {/* Subtle colorful gradient for idle state - thicker border */}
          <div className="absolute -inset-[2px] bg-gradient-to-r from-blue-400/60 via-purple-400/60 to-pink-400/60 rounded-2xl animate-gradient-xy"></div>

          {/* Animated gradient border on focus - thinner and faster */}
          <div className="absolute -inset-[1.5px] bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-2xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-200 blur-sm group-focus-within:animate-gradient-fast"></div>

          {/* Main container */}
          <div className="relative bg-white rounded-2xl shadow-xl shadow-purple-100/20 overflow-hidden transition-all duration-300 group-hover:shadow-2xl group-hover:shadow-purple-100/30 flex flex-col">
            <textarea
              ref={textareaRef}
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              onDragOver={handlers.onDragOver}
              onDragLeave={handlers.onDragLeave}
              onDrop={handlers.onDrop}
              onPaste={handlers.onPaste}
              placeholder={
                buildMode === "Agents" ? "Describe the agent you want to create..." : buildMode === "Workflows" ? "Describe the workflow you want to create..." : "Build a finance app that..."
              }
              className={`w-full px-6 pt-5 pb-2 text-base text-gray-900 placeholder-gray-400 resize-none focus:outline-none min-h-[120px] max-h-[300px] border-0 bg-transparent transition-colors ${
                isDragOver ? "bg-blue-50/50" : ""
              }`}
            />

            {/* Drag overlay */}
            {isDragOver && (
              <div className="absolute inset-0 bg-blue-50/80 backdrop-blur-sm flex items-center justify-center border-2 border-dashed border-blue-400 rounded-2xl">
                <div className="text-center">
                  <Paperclip className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-blue-600 font-medium">Drop files here to attach</p>
                  <p className="text-blue-500 text-sm">or paste files with Ctrl+V</p>
                </div>
              </div>
            )}

            {/* Controls container */}
            <div className="flex items-center justify-between px-4 pb-4">
              {/* Left side controls */}
              <div className="flex items-center gap-2">
                {/* Upload Icon */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-8 h-8 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-all duration-200 flex items-center justify-center"
                      aria-label="Attach file">
                      <Paperclip className="w-4 h-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Attach file</TooltipContent>
                </Tooltip>

                {/* Hidden file input */}
                <input ref={fileInputRef} type="file" multiple onChange={handleFileSelect} className="hidden" accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.xls,.json,.png,.jpg,.jpeg,.gif,.svg" />

                {/* Build Mode Dropdown */}
                <DropdownMenu>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="px-3 h-8 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-all duration-200 text-sm font-medium flex items-center gap-1">
                          {buildMode}
                        </button>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent>Select build mode</TooltipContent>
                  </Tooltip>
                  <DropdownMenuContent side="top" align="start" className="w-40">
                    <DropdownMenuGroup>
                      <DropdownMenuLabel className="text-muted-foreground text-xs">Build Mode</DropdownMenuLabel>
                      <DropdownMenuCheckboxItem
                        checked={buildMode === "Preview"}
                        onCheckedChange={checked => {
                          if (checked) handleBuildModeChange("Preview");
                        }}>
                        Preview
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={buildMode === "App"}
                        onCheckedChange={checked => {
                          if (checked) handleBuildModeChange("App");
                        }}>
                        <span className="flex items-center gap-2">
                          App
                          <Badge variant="secondary" className="h-5 rounded-sm bg-blue-100 px-1.5 text-[10px] text-blue-800">
                            Beta
                          </Badge>
                        </span>
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={buildMode === "Agents"}
                        onCheckedChange={checked => {
                          if (checked) handleBuildModeChange("Agents");
                        }}>
                        Agents
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={buildMode === "Workflows"}
                        onCheckedChange={checked => {
                          if (checked) handleBuildModeChange("Workflows");
                        }}>
                        Workflows
                      </DropdownMenuCheckboxItem>
                    </DropdownMenuGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Right side controls */}
              <div className="flex items-center">
                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={!prompt.trim() || isGenerating || isUploading || (user && !isModuleEnabled ? true : false)}
                  className="w-8 h-8 bg-blue-500 text-white rounded-full hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md flex items-center justify-center"
                  aria-label="Submit">
                  {isGenerating || isUploading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <svg className="w-4 h-4" fill="none" strokeWidth="2.5" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7 11l5-5 5 5M12 16V6" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Attached Files Preview */}
      {attachedFiles.length > 0 && (
        <div className="max-w-3xl w-full mx-auto mb-6">
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700">Attached Files ({attachedFiles.length})</h3>
              {isUploading && (
                <div className="flex items-center gap-2 text-xs text-blue-600">
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  Uploading...
                </div>
              )}
            </div>
            <FileUploadHandler attachedFiles={attachedFiles} setAttachedFiles={setAttachedFiles} />
          </div>
        </div>
      )}

      {/* Sample Prompts - Show mode-specific samples */}
      {showSamplePrompts && (
        <div className="max-w-3xl w-full mx-auto">
          <div className="flex flex-col gap-2.5">
            {/* First row - 2 items centered */}
            <div className="flex justify-center gap-2.5">
              {samplePrompts.slice(0, 2).map((sample, index) => (
                <PromptChip
                  key={index}
                  sample={sample}
                  onClick={() => {
                    setPrompt(sample.prompt);
                    textareaRef.current?.focus();
                  }}
                />
              ))}
            </div>
            {/* Second row - 3 items centered */}
            <div className="flex justify-center gap-2.5">
              {samplePrompts.slice(2).map((sample, index) => (
                <PromptChip
                  key={index + 2}
                  sample={sample}
                  onClick={() => {
                    setPrompt(sample.prompt);
                    textareaRef.current?.focus();
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
